import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import { addressesIncludeKey } from '@/_containers';
import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, defaultApiParamStr, queryClient } from '@/_shared';

import type { GovernanceCreateSchema, GovernanceMutationSchema } from './schemas';
import type { GovernanceQueryAllSchema, GovernanceQuerySchema } from './schemas/governance-query-schema';

export function getGovernanceAllQuery(apiParamStr = defaultApiParamStr) {
  return {
    queryKey: ['GOVERNANCE', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<GovernanceQueryAllSchema>({
        endpoint: '/api/v1/governances',
        params: `${apiParamStr}&include=supporting_documentation`,
      });

      return res;
    },
  };
}

export function useGetAllGovernance(apiParamStr: string) {
  return useSuspenseQuery(getGovernanceAllQuery(apiParamStr));
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['GOVERNANCE'] });
};

export function useGetGovernance(id: string) {
  return useSuspenseQuery(getGovernanceQuery(id));
}

export function getGovernanceQuery(id: string) {
  return {
    queryKey: ['GOVERNANCE', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: GovernanceQuerySchema }>({
        endpoint: `/api/v1/governances/${id}`,
        params: `include=supporting_documentation,${addressesIncludeKey('registered_agent_addresses', true)}`,
        preventAuditLog: true,
      });

      return res;
    },
  };
}

export const invalidateGovernanceQuery = (id: string) => {
  queryClient.invalidateQueries({ queryKey: ['GOVERNANCE', id] });
};

export const useGetGovernanceMutation: UseControlledMutation<GovernanceQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['GOVERNANCE_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<GovernanceQueryAllSchema>({
            endpoint: '/api/v1/governances',
            params: { search, per_page },
          });

          return orderBy(res?.data ?? [], ['legal_business_name']) || [];
        },
      });
    },
  });

export const useCreateMutation = () =>
  useMutation<{ data: GovernanceQuerySchema }, unknown, GovernanceCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/governances/',
        payload,
      }),
  });

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: GovernanceQuerySchema }, ApiError, GovernanceMutationSchema>({
    mutationFn: ({ id: bId, ...payload }) =>
      apiClient.request({
        endpoint: `/api/v1/governances/${id || bId}`,
        method: 'PUT',
        payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['AUDIT_REVISION_HISTORY', 'governances', id] });
    },
  });

export const usePatchMutation = () =>
  useMutation<{ data: GovernanceQuerySchema }, ApiError, Partial<GovernanceMutationSchema>>({
    mutationFn: ({ id, ...payload }) =>
      apiClient.request({
        endpoint: `/api/v1/governances/${id}`,
        method: 'PATCH',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['GOVERNANCE_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/governances/${id}`,
        method: 'DELETE',
      }),
  });
